import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import ptBr from "date-fns/locale/pt-BR";

import { FiChevronRight } from "react-icons/fi";
import { FaFilePdf } from "react-icons/fa";
import { Map } from "../../../../components/Map";
import { HeaderTitle } from "../../../../components/HeaderTitle";
import {
  Container,
  Content,
  CardContent,
  BodyContent,
  PictureFarmer,
  NameFarmer,
  ItemMap,
  SelectOption,
  Option,
  Line,
  ItemContent,
  SanitaryPlan,
  TitleSanitaryPlan,
  DownloadFile,
} from "./styles";
import { getPropertyById } from "../../../../services/property";
import { useNotification } from "../../../../hooks/useNotification";
import { IProperty } from "../../interfaces";
import { useTranslate } from "../../../../hooks/useTranslate";
import { Typography } from "@mui/material";

const ViewProperty = () => {
  const params = useParams();
  const { errorNotification } = useNotification();
  const { translate } = useTranslate();

  const [optionActive, setOptionActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState<IProperty>({} as IProperty);

  const boot = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPropertyById(params.id!);

      setProperty(data);
    } catch (error) {
      errorNotification(translate("property.error_load_property"));
    } finally {
      setLoading(false);
    }
  }, [errorNotification, params.id, translate]);

  const ready = useRef(false);

  useEffect(() => {
    if (!ready.current) {
      ready.current = true;
      boot();
    }
  }, [boot]);

  if (loading) return <div />;

  return (
    <Container>
      <HeaderTitle
        title={translate("property.title_view")}
        showIcon
        url="/properties"
      />
      <Content>
        <CardContent>
          <PictureFarmer>
            <img src={property.photo} alt="Farmer" />
            <NameFarmer>
              <p>{property.name}</p>
            </NameFarmer>
          </PictureFarmer>
          <SelectOption>
            <Option
              onClick={() => {
                setOptionActive(true);
              }}
            >
              {optionActive && <FiChevronRight />}
              <p>{translate("property.data_property")}</p>
            </Option>
            <Option
              onClick={() => {
                setOptionActive(false);
              }}
            >
              {!optionActive && <FiChevronRight />}
              <p>{translate("property.production_milk")}</p>
            </Option>
          </SelectOption>
          {optionActive ? (
            <>
              <BodyContent>
                <ItemContent>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {translate("property.placeholder_hectares")}:{" "}
                  </Typography>
                  <span>{property.hectares}</span>
                </ItemContent>
                <ItemContent>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {translate("property.placeholder_area_dairy")}:
                  </Typography>
                  <span>{property.numberProducerRural}</span>
                </ItemContent>
                <ItemContent>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {translate("property.placeholder_herd_size")}:
                  </Typography>
                  <span>{property.mediumHerdSize}</span>
                </ItemContent>
                <ItemContent>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {translate("property.placeholder_type_production")}:
                  </Typography>
                  <span>{property.typeProduction}</span>
                </ItemContent>
                <ItemContent>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {translate("property.placeholder_cnpj")}:
                  </Typography>
                  <span>{property.cnpj}</span>
                </ItemContent>
                <ItemContent>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {translate("property.placeholder_producer_rural")}:
                  </Typography>
                  <span>{property.numberProducerRural}</span>
                </ItemContent>

                <Line />

                <ItemContent>
                  <p>{`${translate("property.placeholder_city")} / ${translate(
                    "property.placeholder_state"
                  )}:`}</p>
                  <span>{`${property.city} / ${property.state}`}</span>
                </ItemContent>
                <ItemContent>
                  <p>{translate("property.placeholder_route_description")}:</p>
                  <span>{property.routeDescription}</span>
                </ItemContent>
              </BodyContent>
              {!!property.latitude && !!property.longitude && (
                <ItemMap>
                  {/* -9.617912009488514, -36.76724985589201 */}
                  <Map
                    coordinates={{
                      lat: Number(property.latitude),
                      lng: Number(property.longitude),
                    }}
                    disableClick
                    scrollWheelZoom={false}
                    dragging={false}
                    zoom={16}
                  />
                </ItemMap>
              )}
            </>
          ) : (
            <BodyContent>
              <ItemContent>
                <p>{translate("property.main_veterinary")}</p>
                <span>{property.nameVeterinary}</span>
              </ItemContent>
              <SanitaryPlan>
                <TitleSanitaryPlan>
                  {translate("property.sanitary_plan")}
                </TitleSanitaryPlan>
                <DownloadFile
                  target="_blank"
                  href={property.sanitaryPlan}
                  download="Plano Sanitario.pdf"
                >
                  <FaFilePdf />
                  <div>
                    {!!property?.sanitaryPlan && (
                      <p>{`${property?.sanitaryPlan?.slice(
                        0,
                        30
                      )}...${property?.sanitaryPlan?.slice(-20)}`}</p>
                    )}

                    <span>
                      {translate("property.sended")}{" "}
                      {format(
                        parseISO(property.createdAt),
                        "dd/MM/yyyy 'às' HH:mm",
                        {
                          locale: ptBr,
                        }
                      )}
                    </span>
                  </div>
                </DownloadFile>
              </SanitaryPlan>
            </BodyContent>
          )}
        </CardContent>
      </Content>
    </Container>
  );
};

export { ViewProperty };
